import { type RunVisibility } from '@d2/schema';
import { useState, type CSSProperties, type ReactElement } from 'react';

import {
  DEFAULT_VISIBILITY,
  VISIBILITY_CHOICES,
  VISIBILITY_LEGEND,
  type ExperienceReadout,
  type Meter,
  type TownScreenProps,
  describeCharacter,
  describeExperience,
  describeGold,
  descendLabelOf,
  experienceReadoutOf,
  goldMeterOf,
  isDescending,
  levelLabel,
  meterReadout,
  servicesNoteOf,
  statusNoteOf,
  unopenedServices,
  visibilityLabel,
  visibilityNoteOf,
} from './town-screen-model';

export {
  type ExperienceReadout,
  type Meter,
  type RunOutcome,
  type TownCharacterView,
  type TownScreenProps,
} from './town-screen-model';

/**
 * Town: where a character stands between runs.
 *
 * ## It is a screen, not a room
 *
 * Decided 2026-08-13: town is React, not a walkable Colyseus room. Every duty
 * `docs/GAME-DESIGN.md` gives it — buy, sell, repair, stash — is panel work,
 * and a walkable town would duplicate the lobby and party work M7 has to do
 * anyway. So this is not an overlay: unlike `hud.tsx` and `skill-tree.tsx`
 * there is no live canvas underneath it to protect, nothing to swallow a click
 * on the way down, and no `pointer-events` decision to get wrong. It is
 * ordinary flow content that occupies the screen while no floor is open.
 *
 * ## It draws numbers. It does not produce them.
 *
 * Every value in `TownCharacterView` arrives already decided by the caller, out
 * of `packages/core`: `experienceForLevel` and `experienceToNextLevel` for the
 * bar, `goldCapacity(level)` for the purse. This file compares nothing against
 * a threshold, and `town-screen-model.ts` — which owns the whole of the
 * arithmetic that is left — states at length why. The one number computed here
 * is a percentage for a bar width, which is presentation.
 *
 * The max-level case is the one that would show: with no next level the caller
 * sends a span of `0`, and a proportion of nothing is `NaN%` — a declaration a
 * browser drops silently, painting an empty bar as a full one. The model's
 * `ExperienceReadout` makes that a state rather than a division, so this file
 * never divides at all.
 *
 * The third case draws **no bar at all**. A caller that could not resolve the
 * experience says so, and today every caller is in that position: the roster is
 * `@d2/schema`'s `CharacterSummary`, which carries a level and gold and no
 * experience anywhere. A bar drawn from nothing is a claim either way — empty
 * reads as *you have earned nothing this level*, full reads as *level 99* — so
 * the row states that it is not known and the trough is not rendered.
 *
 * ## One press is one run
 *
 * `descent.room.ts` answers a second connection playing the same character with
 * **409 `character-already-on-this-floor`**, which this client currently
 * reports to the player as *"the game server could not be reached"*
 * (`TODO.md` #443). A double-press on Descend is exactly that: two joins for
 * one character, and a player told the server is dead about a server that
 * answered precisely. So while the caller says a run is opening, the control is
 * a genuinely `disabled` button — not one styled to look inert — and a disabled
 * button dispatches no click at all.
 *
 * Where `skill-tree.tsx` deliberately uses `aria-disabled` so a locked cell can
 * answer a click with the reason it is locked, there is nothing here for a
 * second press to be told that the button's own label and the live region do
 * not already say. `CharacterScreen.tsx` disables its play buttons on `busy`
 * for the same reason.
 *
 * ## And a run that was refused is said out loud, here
 *
 * The other half of the same story. The room's 409 now arrives as its own
 * `JoinRefusal` (`game/join-refusal.ts`) instead of being flattened into *"the
 * game server could not be reached"*, and this is where a player reads it: the
 * live region that already exists for the busy note, because the two are the
 * same conversation — *opening a run…* and then *that did not open*.
 *
 * The screen renders it and decides nothing about it. It does not disable
 * Descend, does not count attempts and does not retry: the client's decision is
 * to say so and offer the button again, and this screen is where "again" is.
 * Adding a Retry control beside Descend would be two buttons for one intent,
 * which is why the suite pins the count at two.
 *
 * ## So is a run that **ended**, and it is the same region for the opposite
 * reason
 *
 * A death is not a failure — it is the run finishing the way the design says a
 * run finishes (`docs/GAME-DESIGN.md`), and the seat leaves with close code 4200
 * (`game/run-end.ts`). It lands here because this is where the player *is* when
 * it is true, and in the same live region because it is the last line of the
 * same conversation: *opening a run…*, then a floor, then *that run is over*.
 *
 * It **quotes the toll**, which it could not until `SERVER_MESSAGE.runToll`
 * landed (`TODO.md` #486): what the death took is on the outcome the caller
 * hands in, and `game/run-end.ts` turns it into the sentence. Two numbers appear
 * on this screen from two different places and they are never combined — the
 * toll is what was **taken**, off a message, and the purse in the bar a few
 * lines up is what **remains**, off the roster the caller re-read. Subtracting
 * one from the other would produce a balance nobody owns, because the row a
 * caller holds is older than the run the toll was charged in.
 *
 * It **does not disable Descend**: dying is not a state a player has to be
 * released from, and a screen that latched a death would be a screen you can
 * only leave by signing out.
 *
 * ## One press is also one *decision about strangers*
 *
 * A run is now either advertised in the directory or reachable only by the code
 * the server mints for it, and this screen is where that is chosen — because
 * this is where the press is. It is the one piece of state this file holds, and
 * it is held for the reason `SignInScreen` holds an email: it is what a control
 * is currently set to, not a fact about the world, and nothing above this screen
 * has any use for it until Descend is pressed. So it travels as `onDescend`'s
 * argument rather than as a prop and a callback, which also makes it impossible
 * for a caller to descend under a choice from an earlier render.
 *
 * **It starts private and no code path makes it public by itself.** A listed run
 * publishes this character's name and level to anybody holding an account
 * (`@d2/schema`'s `RunListing`), and nothing about the word *public* warns
 * anybody of that — so the option that costs something is the one that has to be
 * chosen, and the note beside it says what it costs in as many words.
 *
 * ## And where other people's runs are
 *
 * `lobby` is a node, exactly as `vendor` is, and for the same reason: the thing
 * that reads the directory holds a token, and a town screen that took a run list
 * as a prop would be a town screen that could get a join wrong. It stands
 * directly under Descend, because *"start a run"* and *"join one"* are the same
 * decision made two ways and a player choosing between them should not have to
 * go anywhere else. Town is the only screen that can hold both: a lobby of its
 * own would be a fourth screen whose whole content is a list that is usually
 * empty, reachable only from here, and leaveable only back to here.
 *
 * ## A vendor, if the caller has one. Still no stash.
 *
 * GAME-DESIGN gives town three duties and until 2026-08-14 **none of them had an
 * API**, which is why all three were named here as text and none as a control:
 * building the UI first would have been inventing those contracts from the wrong
 * end, in the layer least entitled to.
 *
 * Two of the three now have one. `apps/api`'s vendor buys, sells and repairs,
 * so the counter goes where the placeholder was — as an opaque `vendor` node,
 * because the thing that talks to the API holds a token and this screen must
 * not (`vendor-counter.tsx`). Town gives it a place to stand and knows nothing
 * else about it.
 *
 * **The stash is still named and still offered as nothing**, and the tripwire
 * moved rather than being removed: whatever is left on that list must hold no
 * control at all (`town-screen.test.tsx`). The reason is the same one it always
 * was — there is no route between a stash and a pack, so a button there would
 * be a contract invented from the wrong end.
 *
 * ## Placeholder art
 *
 * The palette is `skill-tree.tsx`'s, copied rather than imported, and styles
 * are inline for the reason that file gives: a shared style module written
 * twice by two agents at once is a merge conflict rather than a saving.
 * Reconciling the three panels is a later, deliberate step.
 */

const PARCHMENT = '#d8d2c4';
const INK = '#0b0b0d';
const PANEL = '#15151a';
const EDGE = '#3a3730';
const GOLD = '#c8a44c';
const DIM = '#6f6a5e';

const SCREEN: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: '40rem',
  margin: '0 auto',
  padding: '1.25rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 4,
  background: INK,
  color: PARCHMENT,
};

const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', gap: '1rem' };
const TITLE: CSSProperties = { margin: 0, fontSize: '1rem', letterSpacing: '0.3em', textTransform: 'uppercase' };
const QUIET_BUTTON: CSSProperties = {
  marginLeft: 'auto',
  padding: '0.4rem 0.9rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: 'transparent',
  color: PARCHMENT,
  font: 'inherit',
  cursor: 'pointer',
};

const CARD: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '0.9rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: PANEL,
};

const NAME: CSSProperties = { margin: 0, fontSize: '1.25rem', fontWeight: 600 };
const META: CSSProperties = { margin: 0, color: DIM, fontSize: '0.85rem' };
const ROW: CSSProperties = { display: 'flex', alignItems: 'baseline', gap: '0.5rem', fontSize: '0.8rem' };
const READOUT: CSSProperties = { marginLeft: 'auto', color: GOLD, fontVariantNumeric: 'tabular-nums' };

const TROUGH: CSSProperties = {
  position: 'relative',
  height: '0.55rem',
  marginTop: '0.25rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: INK,
  overflow: 'hidden',
};

const fillStyle = (percent: number, colour: string): CSSProperties => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: `${percent}%`,
  background: colour,
});

const descendStyle = (busy: boolean): CSSProperties => ({
  padding: '0.7rem 1rem',
  border: `1px solid ${busy ? EDGE : GOLD}`,
  borderRadius: 3,
  background: busy ? PANEL : EDGE,
  color: busy ? DIM : PARCHMENT,
  font: 'inherit',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: busy ? 'progress' : 'pointer',
});

const STATUS: CSSProperties = { minHeight: '1.25rem', margin: 0, color: GOLD, fontSize: '0.85rem' };

const CHOICE_SET: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  margin: 0,
  padding: '0.6rem 0.9rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: PANEL,
};

const LEGEND: CSSProperties = { padding: '0 0.35rem', color: DIM, fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase' };
const CHOICE: CSSProperties = { display: 'flex', alignItems: 'baseline', gap: '0.5rem', fontSize: '0.8rem' };
const CHOICE_NOTE: CSSProperties = { color: DIM, fontSize: '0.75rem' };
const SERVICES: CSSProperties = { ...CARD, gap: '0.35rem', color: DIM, fontSize: '0.8rem' };
const SERVICE_LIST: CSSProperties = { display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: 0, padding: 0, listStyle: 'none' };

/**
 * One labelled bar.
 *
 * The percentage lands on the element twice — once as the width a browser
 * paints and once as `data-fill`, a number a test can read without parsing a
 * style attribute. Both come from the same `Meter`, so they cannot disagree.
 *
 * Every number is on the element as words as well: `aria-valuetext` carries the
 * same sentence the readout beside the bar does, because a fill width and a
 * colour are readable to exactly one kind of player.
 */
interface BarProps {
  readonly id: 'gold' | 'experience';
  readonly label: 'Gold' | 'Experience';
  readonly colour: string;
  readonly percent: number;
  readonly valueNow: number;
  readonly valueMax: number;
  readonly readout: string;
  readonly description: string;
}

const Bar = ({
  id,
  label,
  colour,
  percent,
  valueNow,
  valueMax,
  readout,
  description,
}: BarProps): ReactElement => (
  <div>
    <div style={ROW}>
      <span>{label}</span>
      <span data-testid={`town-${id}-readout`} style={READOUT}>
        {readout}
      </span>
    </div>
    <div
      data-testid={`town-${id}`}
      style={TROUGH}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={valueMax}
      aria-valuenow={valueNow}
      aria-valuetext={description}
    >
      <div data-testid={`town-${id}-fill`} data-fill={percent} style={fillStyle(percent, colour)} />
    </div>
  </div>
);

/**
 * A labelled row with no bar under it, for a number nobody has.
 *
 * Deliberately the same two elements the top line of a `Bar` is, so an unknown
 * readout sits in the column its neighbours do rather than looking like a
 * different kind of thing that has gone wrong.
 */
const Unmeasured = ({ label, readout }: { readonly label: string; readonly readout: string }): ReactElement => (
  <div style={ROW}>
    <span>{label}</span>
    <span data-testid="town-experience-readout" style={{ ...READOUT, color: DIM }}>
      {readout}
    </span>
  </div>
);

/** A readout that has two numbers behind it, which is what a bar needs. */
type MeasuredExperience = Exclude<ExperienceReadout, { readonly kind: 'unknown' }>;

/**
 * The experience bar's three numbers.
 *
 * A max-level bar is drawn full against a span of one — there is no span to
 * take a proportion of, and *full* is the honest picture of a character with
 * nothing left to earn. The `1` is a bound for the progressbar role, not a
 * quantity of anything, which is why it never reaches the readout.
 *
 * It takes a `MeasuredExperience` rather than an `ExperienceReadout`, so an
 * unknown one cannot reach it by accident: there is no bar to draw, and the
 * type says so rather than a comment.
 */
const experienceBar = (readout: MeasuredExperience): Pick<BarProps, 'percent' | 'valueNow' | 'valueMax' | 'readout'> =>
  readout.kind === 'max-level'
    ? { percent: 100, valueNow: 1, valueMax: 1, readout: describeExperience(readout) }
    : {
        percent: readout.meter.percent,
        valueNow: readout.meter.current,
        valueMax: readout.meter.maximum,
        readout: meterReadout(readout.meter),
      };

const goldBar = (meter: Meter): Pick<BarProps, 'percent' | 'valueNow' | 'valueMax' | 'readout'> => ({
  percent: meter.percent,
  valueNow: meter.current,
  valueMax: meter.maximum,
  readout: meterReadout(meter),
});

export const TownScreen = ({
  character,
  onDescend,
  onSignOut,
  descending,
  outcome = null,
  vendor = null,
  lobby = null,
}: TownScreenProps): ReactElement => {
  // The one piece of state on this screen: what the control is set to. It
  // starts private and only a press of one of the two radios moves it — see the
  // header for why that is not a preference.
  const [visibility, setVisibility] = useState<RunVisibility>(DEFAULT_VISIBILITY);
  const busy = isDescending(descending);
  const experience = experienceReadoutOf(character);
  const gold = goldMeterOf(character);
  // A caller with no counter passes nothing, and the default above makes that
  // `null`. Anything it *did* pass is a counter, and the services list below is
  // what changes because of it.
  const vendorOpen = vendor !== null;

  return (
    <section data-testid="town-screen" aria-label="Town" style={SCREEN}>
      <header style={HEADER}>
        <h2 style={TITLE}>Town</h2>
        <button
          data-testid="town-sign-out"
          type="button"
          style={QUIET_BUTTON}
          // Wrapped rather than passed straight through: the contract is
          // `() => void`, and handing it React's synthetic event would make a
          // caller's `onSignOut` take an argument it never asked for.
          onClick={(): void => {
            onSignOut();
          }}
        >
          Sign out
        </button>
      </header>

      <div role="group" aria-label={describeCharacter(character)} style={CARD}>
        <div>
          <p data-testid="town-character-name" style={NAME}>
            {character.name}
          </p>
          <p data-testid="town-character-meta" style={META}>
            {`Level ${levelLabel(character.level)} · ${character.characterClass}`}
          </p>
        </div>

        {experience.kind === 'unknown' ? (
          <Unmeasured label="Experience" readout={describeExperience(experience)} />
        ) : (
          <Bar
            id="experience"
            label="Experience"
            colour={PARCHMENT}
            description={describeExperience(experience)}
            {...experienceBar(experience)}
          />
        )}
        <Bar id="gold" label="Gold" colour={GOLD} description={describeGold(gold)} {...goldBar(gold)} />
      </div>

      {/*
        A `fieldset` and radios rather than a toggle, because the two options
        are not a thing and its absence: *"anybody can find this"* and *"only
        the code gets in"* are two states a player picks between, and each one
        needs a sentence of its own beside it.
      */}
      <fieldset data-testid="town-visibility" style={CHOICE_SET}>
        <legend style={LEGEND}>{VISIBILITY_LEGEND}</legend>
        {VISIBILITY_CHOICES.map((choice) => (
          <label key={choice} style={CHOICE}>
            <input
              data-testid={`town-visibility-${choice}`}
              type="radio"
              name="run-visibility"
              value={choice}
              checked={visibility === choice}
              // Inert while a run is opening: the choice went out with the
              // press, so a control that still moved would be describing a run
              // other than the one being opened.
              disabled={busy}
              onChange={(): void => {
                setVisibility(choice);
              }}
            />
            <span>{visibilityLabel(choice)}</span>
            <span style={CHOICE_NOTE}>{visibilityNoteOf(choice)}</span>
          </label>
        ))}
      </fieldset>

      <button
        data-testid="town-descend"
        type="button"
        // A real `disabled`, not a look. See the header: the second press is a
        // second join for one character, and the room answers it 409.
        disabled={busy}
        style={descendStyle(busy)}
        onClick={(): void => {
          // The choice as it stands at the press. Read here rather than
          // reported on change, so a caller cannot open a run under a choice
          // from an earlier render.
          onDescend(visibility);
        }}
      >
        {descendLabelOf(descending)}
      </button>

      {/*
        One region, three things that can want it, and the model decides which —
        a run that is opening, the reason the last one did not, or the news that
        the last one is over.
      */}
      {/*
        `data-testid` as well as the role, because town is no longer the only
        live region on this screen: the vendor's counter has one of its own, and
        the two are deliberately separate — *"bought a dagger"* and *"that run is
        over"* are two conversations, and one region carrying both would let a
        purchase quietly overwrite the news of a death.
      */}
      <p data-testid="town-status" role="status" aria-live="polite" style={STATUS}>
        {statusNoteOf(descending, outcome)}
      </p>

      {/*
        Other people's runs, when the caller brought a board. Directly under
        Descend, because starting a run and joining one are the same decision
        made two ways. Rendered whole and untouched: town knows it is a node and
        nothing else about it.
      */}
      {lobby}

      {/*
        The counter, when the caller brought one. Rendered whole and untouched:
        town knows it is a node and nothing else about it.
      */}
      {vendor}

      {/*
        Named, not offered, and now down to what is genuinely unbuilt. There is
        still no route between a stash and a pack, so there is nothing here for
        a control to be wired to. See the header.
      */}
      <section data-testid="town-services" aria-label="Town services" style={SERVICES}>
        <ul style={SERVICE_LIST}>
          {unopenedServices(vendorOpen).map((service) => (
            <li key={service}>{service}</li>
          ))}
        </ul>
        <p style={{ margin: 0 }}>{servicesNoteOf(vendorOpen)}</p>
      </section>
    </section>
  );
};
